import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useSettings, digits } from "@/context/SettingsContext";
import { fbEvent } from "@/lib/pixel";
import { CheckCircle2, Truck, MessageCircle, Clock } from "lucide-react";

export default function OrderSuccess() {
  const { order_number } = useParams();
  const [sp] = useSearchParams();
  const [order, setOrder] = useState(null);
  const isGuest = sp.get("guest") === "1";
  const { settings } = useSettings();
  // Never hard-code a WhatsApp number — the redirect always uses the number
  // currently configured in Admin → Contact & WhatsApp Settings.
  const WHATSAPP = digits(settings.whatsapp_number);
  // Set by Checkout right after the customer taps "I Have Paid".
  const [justPaid, setJustPaid] = useState(() => {
    try { return sessionStorage.getItem(`adiyogi_just_paid_${order_number}`) === "1"; } catch { return false; }
  });

  useEffect(() => { api.get(`/orders/${order_number}`).then(({data}) => setOrder(data)); }, [order_number]);

  // After "I Have Paid": show the processing message, then redirect to WhatsApp
  // using the ACTIVE number from admin settings (no hard-coded number).
  // StrictMode-safe: the session flag is only cleared when the timeout actually fires,
  // so a double-invoked effect simply reschedules the same redirect.
  useEffect(() => {
    if (!order?.order_number || !justPaid) return;
    const wa = digits(settings.whatsapp_number);
    if (!wa) return; // wait for settings; never fall back to a hard-coded number
    let flag;
    try { flag = sessionStorage.getItem(`adiyogi_just_paid_${order.order_number}`); } catch {}
    if (flag !== "1") return;
    const msg = `Hello AdiYogi Handcrafts! I have placed my order ${order.order_number} (₹${order.total}) and completed the UPI payment.${order.utr_reference ? ` UTR: ${order.utr_reference}.` : ""} Please confirm my order.`;
    const url = `https://wa.me/${wa}?text=${encodeURIComponent(msg)}`;
    const t = setTimeout(() => {
      try { sessionStorage.removeItem(`adiyogi_just_paid_${order.order_number}`); } catch {}
      setJustPaid(false);
      window.location.href = url;
    }, 2500);
    return () => clearTimeout(t);
  }, [order, settings.whatsapp_number, justPaid]);

  // Meta Pixel: Purchase — fired exactly once per order on the success page.
  useEffect(() => {
    if (!order?.order_number) return;
    const flag = `fbq_purchase_${order.order_number}`;
    try { if (localStorage.getItem(flag)) return; } catch { /* continue */ }
    fbEvent("Purchase", {
      content_ids: (order.items || []).map((i) => i.product_id),
      content_type: "product",
      num_items: (order.items || []).reduce((s, i) => s + i.quantity, 0),
      value: Number(order.total) || 0,
      currency: "INR",
      order_id: order.order_number,
    });
    try { localStorage.setItem(flag, "1"); } catch { /* fine */ }
  }, [order]);

  if (!order) return <div className="container-x py-20 text-center">Loading…</div>;
  const isPaid = order.payment_status === "paid";
  const isAutoVerified = isPaid && (order.auto_verified === true || (order.history || []).some((h) => h.source === "auto_utr"));
  const paymentLabel = isPaid ? (isAutoVerified ? "PAID · Auto-Verified" : "PAID · Verified") : "Verification Pending";
  const heading = isPaid ? "Order Confirmed" : "Payment Submitted";
  const primaryMsg = isPaid
    ? (isAutoVerified
        ? "Your UTR matched — payment verified automatically and your order is now being processed."
        : "Payment verified successfully. Your order is now being processed.")
    : "Your order is being processed. Order will be confirmed shortly after payment verification.";
  const waSupport = WHATSAPP ? `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(`Hello AdiYogi Handcrafts, I have a question about my order ${order.order_number}.`)}` : null;

  return (
    <div className="container-x py-16 max-w-3xl" data-testid="order-success-page">
      <div className="text-center mb-10">
        <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${isPaid ? "bg-emerald-50" : "bg-amber-50"}`}>
          {isPaid
            ? <CheckCircle2 className="w-8 h-8 text-emerald-600" strokeWidth={1.5} />
            : <Clock className="w-8 h-8 text-amber-600" strokeWidth={1.5} />}
        </div>
        <h1 className="font-serif text-3xl md:text-4xl leading-tight" data-testid="order-heading">{heading}</h1>
        <p className="text-muted-foreground mt-3 max-w-lg mx-auto">{primaryMsg}</p>
        <div className="mt-4 text-sm">Order ID: <span className="font-medium gold-text" data-testid="order-number">{order.order_number}</span></div>
        <div className="mt-1 text-xs">
          Order status: <span className="font-medium text-blue-700" data-testid="order-status-chip">{isPaid ? "Processing" : (order.status === "processing" ? "Processing" : "Processing")}</span>
        </div>
        {!isPaid && (
          <div className="mt-2 text-xs text-muted-foreground max-w-md mx-auto">
            Your order will be <span className="text-espresso font-medium">confirmed after payment verification</span>. You&apos;ll hear from us on WhatsApp shortly.
          </div>
        )}
        {justPaid && WHATSAPP && (
          <div className="mt-3 text-xs text-emerald-700 font-medium" data-testid="wa-redirect-notice">
            Redirecting you to WhatsApp to confirm your order…
          </div>
        )}
      </div>

      <div className={`border rounded-xl p-4 mb-6 flex items-center gap-3 ${isPaid ? "border-emerald-200 bg-emerald-50/50" : "border-amber-200 bg-amber-50/50"}`} data-testid="payment-banner">
        <div className={`w-2.5 h-2.5 rounded-full ${isPaid ? "bg-emerald-500" : "bg-amber-500 animate-pulse"}`}></div>
        <div className="flex-1 text-sm">
          <span className="font-medium">Payment status:</span> {paymentLabel}
          {isAutoVerified && <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-emerald-600 text-white px-2 py-0.5 rounded-full" data-testid="auto-verified-badge"><CheckCircle2 className="w-3 h-3" strokeWidth={2} /> Auto</span>}
        </div>
        {order.utr_reference && <div className="text-xs text-muted-foreground">UTR: <span className="font-mono">{order.utr_reference}</span></div>}
      </div>

      <div className="border hairline rounded-xl p-6 bg-white">
        <div className="font-serif text-2xl mb-4">Order Summary</div>
        <div className="space-y-2 mb-4">
          {order.items.map((it) => (
            <div key={it.product_id} className="flex items-center gap-3 text-sm">
              <img src={it.image} className="w-12 h-12 rounded object-cover" alt="" />
              <div className="flex-1">{it.name} <span className="text-xs text-muted-foreground">×{it.quantity}</span></div>
              <div>₹{it.line_total}</div>
            </div>
          ))}
        </div>
        <div className="border-t hairline pt-4 space-y-1 text-sm">
          <div className="flex justify-between"><span>Subtotal</span><span>₹{order.subtotal}</span></div>
          {order.discount > 0 && <div className="flex justify-between text-emerald-700"><span>Discount</span><span>−₹{order.discount}</span></div>}
          <div className="flex justify-between"><span>Shipping</span><span>{order.shipping ? `₹${order.shipping}` : "Free"}</span></div>
          <div className="flex justify-between font-serif text-xl"><span>Total</span><span className="gold-text">₹{order.total}</span></div>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-4 text-sm">
          <div className="border hairline rounded-lg p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Shipping to</div>
            <div>{order.address.full_name}</div>
            {order.address.line1 && <div className="text-muted-foreground">{order.address.line1}{order.address.line2 ? ", " + order.address.line2 : ""}</div>}
            <div className="text-muted-foreground">{order.address.city}, {order.address.state}</div>
            <div className="text-muted-foreground">{order.address.country}</div>
          </div>
          <div className="border hairline rounded-lg p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Payment</div>
            <div>Method: UPI (Prepaid)</div>
            <div>Status: <span className={isPaid ? "text-emerald-700" : "text-amber-700"}>{paymentLabel}</span></div>
            <div className="text-muted-foreground text-xs mt-2 flex items-center gap-1"><Truck className="w-3.5 h-3.5" strokeWidth={1.5} /> Estimated delivery: 3–4 business days after verification</div>
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        <Link to={`/track/${order.order_number}`} className="btn-outline-gold rounded-full px-6 py-2 text-xs uppercase tracking-widest">Track Order</Link>
        {waSupport && <a href={waSupport} target="_blank" rel="noopener noreferrer" className="rounded-full bg-emerald-600 text-white px-6 py-2 text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-emerald-700" data-testid="wa-support"><MessageCircle className="w-4 h-4" strokeWidth={1.5} /> WhatsApp Support</a>}
        <Link to="/shop" className="btn-gold rounded-full px-6 py-2 text-xs uppercase tracking-widest">Continue Shopping</Link>
      </div>
      <div className="mt-8 border-t hairline pt-4 flex items-center gap-3 justify-center opacity-70">
        <img src="/adiyogi-logo.png" alt="AdiYogi Handcrafts" className="h-8 w-auto" />
        <div className="text-xs uppercase tracking-widest text-muted-foreground">AdiYogi Handcrafts · Prepaid Only</div>
      </div>
    </div>
  );
}
